/* assets/mm-hide-soldout-variants.js
 *
 * Minimog-compatible sold-out variant hiding.
 * - Reads variants from #productData (already on your page)
 * - Finds option controls by data-option-position / data-option-index OR common variant-picker patterns
 * - Hides option values that cannot lead to an AVAILABLE variant (not shown as sold out)
 */

(() => {
  // ---------- CONFIG: tweak if your clickable option wrapper differs ----------
  const WRAPPER_SELECTORS = [
    "label",
    "button",
    "li",
    ".m-option__item",
    ".m-product-option__item",
    ".m-variant-option",
    ".m-variant__option",
    ".product-option-item",
    ".variant-picker__option",
    ".variant-picker__item",
  ];

  // Button "selected" heuristics (Minimog often uses aria-pressed or classnames)
  const SELECTED_BUTTON_SELECTORS = [
    '[aria-pressed="true"]',
    ".is-selected",
    ".selected",
    ".active",
  ];
  // --------------------------------------------------------------------------

  const productJsonEl = document.querySelector("#productData");
  if (!productJsonEl) return;

  let productData;
  try {
    productData = JSON.parse(productJsonEl.textContent);
  } catch (e) {
    console.warn("[mm-hide-soldout-variants] Could not parse #productData JSON", e);
    return;
  }

  const variants = Array.isArray(productData?.variants) ? productData.variants : [];
  if (!variants.length) return;

  const esc = (s) => {
    try {
      return CSS.escape(s);
    } catch {
      return String(s).replace(/(["\\])/g, "\\$1");
    }
  };

  const hideNode = (node) => {
    if (!node) return;
    node.classList.add("mm-hidden-soldout");
    node.style.display = "none";
  };

  const showNode = (node) => {
    if (!node) return;
    node.classList.remove("mm-hidden-soldout");
    node.style.display = "";
  };

  const findWrapper = (el) => {
    if (!el) return null;
    for (const sel of WRAPPER_SELECTORS) {
      const w = el.closest(sel);
      if (w) return w;
    }
    return el.parentElement;
  };

  // ---------- Detect option UI on the page ----------
  // We support (in order):
  // 1) Standard Shopify names options[...]
  // 2) data-option-position="1|2|3" OR data-option-index="0|1|2"
  // 3) Minimog "variant-picker" patterns
  const hasStandardOptionNames = () =>
    document.querySelectorAll('input[name^="options["], select[name^="options["]').length > 0;

  const getOptionNamesInOrder_Standard = () => {
    const fields = Array.from(
      document.querySelectorAll('input[name^="options["], select[name^="options["]')
    );
    const names = [];
    const seen = new Set();
    for (const el of fields) {
      const raw = el.getAttribute("name"); // options[Color]
      if (!raw || !raw.startsWith("options[")) continue;
      if (seen.has(raw)) continue;
      seen.add(raw);
      const name = raw.slice(8, -1);
      if (name) names.push(name);
    }
    return names;
  };

  const getCurrentSelections_Standard = (optionNames) => {
    return optionNames.map((optName) => {
      const nameAttr = `options[${optName}]`;
      const checked = document.querySelector(`input[type="radio"][name="${esc(nameAttr)}"]:checked`);
      if (checked) return checked.value || null;
      const select = document.querySelector(`select[name="${esc(nameAttr)}"]`);
      if (select) return select.value || null;
      const any = document.querySelector(`input[name="${esc(nameAttr)}"]`);
      return any ? any.value || null : null;
    });
  };

  // Data-attribute based controls (more likely in your case)
  const getOptionPositions = () => {
    // If we see data-option-position values, use them (1-based)
    const posEls = Array.from(document.querySelectorAll("[data-option-position]"));
    const positions = new Set();
    posEls.forEach((el) => {
      const v = parseInt(el.getAttribute("data-option-position"), 10);
      if (Number.isFinite(v) && v >= 1 && v <= 3) positions.add(v);
    });
    if (positions.size) return Array.from(positions).sort((a, b) => a - b);

    // Else try data-option-index (0-based)
    const idxEls = Array.from(document.querySelectorAll("[data-option-index]"));
    const indexes = new Set();
    idxEls.forEach((el) => {
      const v = parseInt(el.getAttribute("data-option-index"), 10);
      if (Number.isFinite(v) && v >= 0 && v <= 2) indexes.add(v);
    });
    if (indexes.size) return Array.from(indexes).sort((a, b) => a - b).map((i) => i + 1);

    // Fallback: assume up to product options length
    const optionCount = Array.isArray(productData?.options) ? productData.options.length : 0;
    return [1, 2, 3].slice(0, Math.min(3, optionCount || 3));
  };

  const getControlsForPosition = (position) => {
    // Collect likely clickable/choice elements for this option position
    const selectors = [
      // controls themselves
      `input[data-option-position="${position}"]`,
      `select[data-option-position="${position}"]`,
      `button[data-option-position="${position}"]`,
      `[data-option-position="${position}"] input`,
      `[data-option-position="${position}"] select`,
      `[data-option-position="${position}"] button`,

      // 0-based index support
      `input[data-option-index="${position - 1}"]`,
      `select[data-option-index="${position - 1}"]`,
      `button[data-option-index="${position - 1}"]`,
      `[data-option-index="${position - 1}"] input`,
      `[data-option-index="${position - 1}"] select`,
      `[data-option-index="${position - 1}"] button`,
    ];

    const els = [];
    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => els.push(el));
    });

    // Deduplicate
    return Array.from(new Set(els)).filter((el) => {
      const tag = el.tagName;
      return tag === "INPUT" || tag === "SELECT" || tag === "BUTTON";
    });
  };

  const getControlValue = (el) => {
    if (!el) return null;
    if (el.tagName === "SELECT") return el.value || null;
    if (el.tagName === "INPUT") return el.value || el.getAttribute("data-value") || null;
    if (el.tagName === "BUTTON") {
      return el.getAttribute("data-value") || el.value || el.textContent?.trim() || null;
    }
    return null;
  };

  const isControlSelected = (el) => {
    if (!el) return false;
    if (el.tagName === "INPUT") {
      if (el.type === "radio" || el.type === "checkbox") return !!el.checked;
      return false;
    }
    if (el.tagName === "SELECT") return true; // selection comes from select.value
    if (el.tagName === "BUTTON") {
      if (el.getAttribute("aria-pressed") === "true") return true;
      return SELECTED_BUTTON_SELECTORS.some((sel) => el.matches(sel));
    }
    return false;
  };

  const setControlSelected = (el) => {
    if (!el) return;
    if (el.tagName === "INPUT") {
      if (el.type === "radio" || el.type === "checkbox") {
        el.checked = true;
        el.dispatchEvent(new Event("change", { bubbles: true }));
      }
    } else if (el.tagName === "SELECT") {
      // caller should set el.value first
      el.dispatchEvent(new Event("change", { bubbles: true }));
    } else if (el.tagName === "BUTTON") {
      el.click();
    }
  };

  const getCurrentSelections_ByPosition = (positions) => {
    return positions.map((pos) => {
      const controls = getControlsForPosition(pos);
      const select = controls.find((c) => c.tagName === "SELECT");
      if (select) return select.value || null;

      // prefer checked radio
      const checkedRadio = controls.find(
        (c) => c.tagName === "INPUT" && c.type === "radio" && c.checked
      );
      if (checkedRadio) return checkedRadio.value || null;

      // selected button
      const selectedBtn = controls.find((c) => c.tagName === "BUTTON" && isControlSelected(c));
      if (selectedBtn) return getControlValue(selectedBtn);

      // fallback: if only one control exists, use its value
      if (controls.length === 1) return getControlValue(controls[0]);

      return null;
    });
  };

  // ---------- Variant logic ----------
  const matchesOtherSelections = (variant, selections, skipIndex) => {
    for (let i = 0; i < selections.length; i++) {
      if (i === skipIndex) continue;
      const selVal = selections[i];
      if (!selVal) continue;
      const vVal = variant[`option${i + 1}`];
      if (vVal !== selVal) return false;
    }
    return true;
  };

  const allowedValuesForIndex = (selections, optionIndex) => {
    const allowed = new Set();
    for (const v of variants) {
      if (!v.available) continue;
      if (!matchesOtherSelections(v, selections, optionIndex)) continue;
      const val = v[`option${optionIndex + 1}`];
      if (val) allowed.add(val);
    }
    return allowed;
  };

  const updateUI_ByPosition = () => {
    const positions = getOptionPositions();
    const selections = getCurrentSelections_ByPosition(positions);

    positions.forEach((pos, idx) => {
      const allowed = allowedValuesForIndex(selections, idx);
      const controls = getControlsForPosition(pos);

      // If there's a select for this option: rebuild/hide options
      const select = controls.find((c) => c.tagName === "SELECT");
      if (select) {
        // store original once
        if (!select.dataset.mmOriginalOptions) {
          select.dataset.mmOriginalOptions = JSON.stringify(
            Array.from(select.options).map((o) => ({ value: o.value, text: o.text, disabled: o.disabled }))
          );
        }
        const original = JSON.parse(select.dataset.mmOriginalOptions || "[]");
        const current = select.value;

        select.innerHTML = "";
        original.forEach((o) => {
          if (!o.value) {
            const opt = document.createElement("option");
            opt.value = o.value;
            opt.textContent = o.text;
            opt.disabled = o.disabled;
            select.appendChild(opt);
            return;
          }
          if (!allowed.has(o.value)) return;
          const opt = document.createElement("option");
          opt.value = o.value;
          opt.textContent = o.text;
          opt.disabled = o.disabled;
          select.appendChild(opt);
        });

        if (allowed.has(current)) select.value = current;
      }

      // Radios / buttons: hide wrappers
      controls.forEach((c) => {
        if (c.tagName === "SELECT") return;

        const val = getControlValue(c);
        if (!val) return;

        const wrapper = findWrapper(c);
        if (allowed.has(val)) {
          showNode(wrapper);
        } else {
          // unselect if selected
          if (c.tagName === "INPUT" && (c.type === "radio" || c.type === "checkbox") && c.checked) {
            c.checked = false;
          }
          hideNode(wrapper);
        }
      });
    });

    // Snap selection to first available variant if current selection can't match anything available
    const firstAvailable = variants.find((v) => v.available);
    if (!firstAvailable) return;

    const current = getCurrentSelections_ByPosition(positions);
    const canMatch = variants.some((v) => {
      if (!v.available) return false;
      return current.every((val, i) => !val || v[`option${i + 1}`] === val);
    });

    if (!canMatch || current.some((v) => !v)) {
      positions.forEach((pos, i) => {
        const wanted = firstAvailable[`option${i + 1}`];
        if (!wanted) return;
        const controls = getControlsForPosition(pos);

        const select = controls.find((c) => c.tagName === "SELECT");
        if (select) {
          select.value = wanted;
          setControlSelected(select);
          return;
        }

        const radio = controls.find(
          (c) => c.tagName === "INPUT" && c.type === "radio" && c.value === wanted
        );
        if (radio) {
          setControlSelected(radio);
          return;
        }

        const btn = controls.find((c) => c.tagName === "BUTTON" && getControlValue(c) === wanted);
        if (btn) {
          setControlSelected(btn);
        }
      });
    }
  };

  const updateUI = () => {
    // If standard Shopify option fields exist, you can extend this later.
    // In your case, they don't, so we use position-based.
    updateUI_ByPosition();
  };

  // Run and re-run when variant picker changes
  const onChange = (e) => {
    const t = e.target;
    if (!t) return;

    // Update if user interacts with inputs/selects/buttons inside product area
    if (
      t.matches("input, select, button") ||
      t.closest("variant-picker") ||
      t.closest("form[action*='/cart/add']") ||
      t.closest(".m-main-product")
    ) {
      requestAnimationFrame(updateUI);
    }
  };

  document.addEventListener("change", onChange);
  document.addEventListener("click", (e) => {
    const t = e.target;
    if (!t) return;
    // Button-based option pickers often change on click, not change
    if (t.closest("button")) requestAnimationFrame(updateUI);
  });

  window.addEventListener("DOMContentLoaded", updateUI);
  updateUI();
})();
